import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
    Button, Textarea, Modal, Spinner,
} from 'flowbite-react';
import axios, { AxiosProgressEvent } from 'axios';
import { useRecoilState } from 'recoil';

import WarningCard from '@/components/commons/warning_card';
import File from '@/components/commons/icons/File';
import { BIO_NAMES, BIO_STATES, reportGeneralError } from '@/components/onboarding/Shared';
import routes from '@/services/routes';
import {
    CREATE_BIO_BATCH, FETCH_BIO_BATCH, UPDATE_USER, updateBriefDirect,
} from '@/components/onboarding/Actions';
import PanelContainer from '@/components/commons/panel_container';
import { BioCandidateStatusEnum, Brief, userAtom } from '@/components/with_auth/State';
import { type bioPageProps } from '@/types/globals';
import { BIO_GEN_TEMPERATURE } from '@/utils/index';

import personalizeStyles from './Personalize.module.css';
import BioLabel from './Label';
import ProgressBar from './ProgressBar';
import FilePanel from './FilePanel';
import ButtonSansStyle from './ButtonSansStyles';
import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import customTextAreaTheme from './TextAreaTheme';
import customLinkedInModalTheme from './LinkedInModalTheme';
import customModalTheme from './UploadFileModalTheme';

function SelectImportMethodPanel({
    isModal = false,
    isEdit,
    openBrief,
    extendContinue = null,
    overrideBack = null,
}: bioPageProps) {
    const router = useRouter();

    const [modalType, setModalType] = useState(null);
    const [importData, setImportData] = useState({ linkedIn: null, PDF: null, copyAndPaste: '' });
    const [isCopyAndPasteActive, setIsCopyAndPasteActive] = useState(true);
    const [focusCopyAndPaste, setFocusCopyAndPaste] = useState(false);
    const [progress, setProgress] = useState(null);
    const [linkedInHelpModalVisible, setLinkedInHelpModalVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [newId, setNewId] = useState(-1);

    const [checkBios, setCheckBios] = useState(false);
    const [generateTimeout, setGenerateTimeout] = useState(false);
    const [noDataModal, setNoDataModal] = useState(false);
    const [user, setUser] = useRecoilState(userAtom);

    const [brief, setBrief] = useState<Brief>(openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex)
        : user.briefs[0]);

    useEffect(() => {
        const currentBrief = openBrief
            ? user.briefs.find((item) => item.id === openBrief.briefIndex)
            : user.briefs[0];

        if (currentBrief) {
            setBrief(currentBrief);
        }
    }, [user, openBrief]);

    const [updateUser] = useMutation(UPDATE_USER);

    const [
        createBioBatches,
        { error: createBioBatchError, reset: resetCreateBioBatch },
    ] = useMutation(CREATE_BIO_BATCH);

    const [getBioBatches, { data: bioBatchData }] = useLazyQuery(FETCH_BIO_BATCH, {
        // automatically go to the server and poll every 10 seconds
        pollInterval: 10 * 1000,
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (!bioBatchData) {
            return;
        }

        if (newId !== brief.bioBatch?.id) {
            return;
        }

        // specific brief
        // get candidate statuses
        const currentBioBatchId = brief.bioBatch?.id;

        const currentBioBatch = bioBatchData.user.bioBatches.filter(
            (bio) => bio.id === currentBioBatchId,
        );
        const candidatesStatuses = currentBioBatch.map(
            (bio) => bio.candidates.map((candidate) => candidate.status),
        ).flat();

        const status = currentBioBatch.map(
            (bio) => bio.status,
        );

        const nodataStatus = status.filter(
            (item) => item === 'nodata',
        );

        // check for at least one successful status

        if (nodataStatus.length > 0) {
            setCheckBios(false);
            setNoDataModal(true);
            return;
        }

        // if we have no candidates yet, don't move forward
        if (!candidatesStatuses.length) {
            return;
        }

        // check for at least one successful status
        const hasSuccessStatus = candidatesStatuses.some(
            (item) => item === BioCandidateStatusEnum.Succeeded,
        );

        if (hasSuccessStatus) {
            // at least one bio candidate is successful, go to the next page
            if (isModal && extendContinue) {
                // go forward
                extendContinue('', isEdit);
            } else {
                updateUser({
                    variables: {
                        updateUserInput: {
                            attributes: {
                                onboardingState: BIO_STATES.selectBio,
                            },
                        },
                    },
                });
                router.push(routes.BIO_SELECT_STARTER_BIO);
            }
        }
        // NOTE(tom) WARNING!!!!!!! Don't add more deps to this list,
        // it may cause this to run twice and mess with navigation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bioBatchData]);

    useEffect(() => {
        if (createBioBatchError) {
            setFormError('An unexpected error occurred.');
            resetCreateBioBatch();
            setCheckBios(false);
            setProgress(null);
            // TODO(tom) what do we do here if the createBioBatches called errored out
            // if we try again we create a loop the user can't get out of if there's
            // a server error, if we hide the loader at least they can go back to manual entry
        }
    }, [createBioBatchError, resetCreateBioBatch]);

    // set a timeout if the generate candidates never works
    // so the user doesn't get stuck and can skip ahead
    useEffect(() => {
        // skip this timeout unless we are checking bios
        if (!checkBios) {
            return;
        }
        // timeout after 15 seconds of waiting
        const timeout = 15 * 1000;
        // set a timeout if we never get candidates
        const timerId = setTimeout(() => {
            setGenerateTimeout(true);
        }, timeout);

        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(timerId);
        };
    }, [checkBios]);

    const handleFileEditClick = (type) => {
        setModalType(type);
    };

    const handleCopyAndPasteEditClick = () => {
        setIsCopyAndPasteActive(true);
        setFocusCopyAndPaste(true);
    };

    const handleFileRemoveClick = (type) => {
        setImportData((prevState) => ({
            ...prevState,
            [type]: null,
        }));
    };

    const handleAddFileClick = (type) => {
        setModalType(type);
    };

    const validateFileExt = (file) => {
        if (!file) {
            return null;
        }

        // validate the file extensions, note this can't
        // verify the CONTENTS of the file, just the extension
        // someone could name a JPG .zip, we need more robust
        // MIME checks for that, which could even then be inaccurate
        if (modalType === 'PDF' && !file.name.includes('.pdf')) {
            return 'A .pdf file was expected for PDF upload.';
        }
        if (modalType === 'linkedIn' && !file.name.includes('.zip')) {
            return 'A .zip file was expected for LinkedIn data.';
        }

        return null;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        const error = validateFileExt(file);

        if (!error) {
            setFormError('');
            setImportData({
                ...importData,
                [modalType]: file || null,
            });
        } else {
            setFormError(error);
        }
        setModalType(null);
    };

    const handleCopyAndPasteChange = (value) => {
        setImportData({
            ...importData,
            copyAndPaste: value,
        });
    };

    const handleBackNavigation = () => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_CAREER_HISTORY);
        }
    };

    const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

    const handleContinue = async () => {
        if (!importData.copyAndPaste && !importData.PDF && !importData.linkedIn) {
            // nothing to upload...
            setFormError('Please select a file or enter text to upload');
            return false;
        }
        if (formError !== '') setFormError('');
        setIsLoading(true);

        const data = new FormData();

        if (importData.copyAndPaste) {
            const file = new Blob([importData.copyAndPaste], { type: 'text/plain' });
            data.append('copyAndPasteData', file);
        }

        if (importData.PDF) {
            const file = importData.PDF;
            data.append(file.name, file);
        }
        if (importData.linkedIn) {
            const file = importData.linkedIn;
            data.append(file.name, file);
        }

        try {
            const response = await axios.post(routes.BIO_UPLOAD, data, {
                withCredentials: true,
                onUploadProgress(e: AxiosProgressEvent) {
                    setProgress({
                        complete: Math.round((e.loaded * 100) / e.total),
                        completeInMb: bytesToMB(e.loaded),
                    });
                },
            });
            if (response) {
                const bioBatchesRes = await createBioBatches({
                    variables: {
                        input: {
                            attributes: {
                                temperature: BIO_GEN_TEMPERATURE,
                            },
                        },
                    },
                });
                if (formError !== '') setFormError('');
                const { id } = bioBatchesRes.data.createBioBatch.bioBatch;
                await updateBriefDirect(
                    brief.id,
                    { bioBatchId: id, skippedBioGeneration: false },
                    { user, setUser },
                );
                setCheckBios(true);
                setNewId(id);
                await getBioBatches();
            }
        } catch (err) {
            reportGeneralError(err);
        }
        setIsLoading(false);
        return true;
    };

    const skipGenerate = () => {
        if (isModal && extendContinue) {
            extendContinue('skip', isEdit);
        } else {
            router.push(routes.BIO_EDIT_BIO);
        }
    };

    const renderLinkedInModal = () => (
        <Modal dismissible theme={customLinkedInModalTheme} className="p-0 m-0 font-instrument absolute z-[1000] focus:outline-none focus-visible:outline-none focus-visible:ring-0 overflow" show={linkedInHelpModalVisible} onClose={() => setLinkedInHelpModalVisible(false)} position="center" size="3xl">
            <div className="flex justify-between border-gray-200 border-b rounded items-center h-[75px]">
                <p className="text-[18px] font-semibold leading-20 pl-6">How to share your LinkedIn data with Sixty</p>
                <button className="pr-6 py-6" type="button" onClick={() => setLinkedInHelpModalVisible(false)}><img src="/images/icon-closeButton.svg" alt="close" /></button>
            </div>
            <div className="flex flex-col p-6 pt-6 text-cleanSlate">
                {/* logo | explanation */}
                <div className="flex flex-col md:flex-row">
                    <img className="w-[60px] h-[60px] my-1" src="/images/bio/goose-avatar.png" alt="goose-avatar" />

                    <div className="md:pl-4">
                        <p>
                            I can use your LinkedIn profile data to draft a more accurate bio.
                            To help me with this task, here&apos;s the info I need from LinkedIn,
                            and how to get it. (Remember, your data belongs to you. You can edit or
                            delete this data at any time.)
                        </p>
                        We can use your LinkedIn profile data to draft a more accurate bio.
                        Here&apos;s how you can pull this information from LinkedIn.
                        Your data is yours and you can edit or delete it at any data.
                        {/* bullet list */}
                        <ul className="py-4 pl-2 list-[square] list-inside">
                            <li>
                                Full export of LinkedIn data,
                                {' '}
                                <b>or</b>
                            </li>
                            <li>Recommendations and Profile data</li>
                        </ul>
                        <p>
                            To export your data, log in to LinkedIn, then go to
                            {' '}
                            <a href="https://www.linkedin.com/mypreferences/d/download-my-data" className="text-progressBarBlue break-words">https://www.linkedin.com/mypreferences/d/download-my-data</a>
                            .
                        </p>
                        <div className="py-5 md:w-[90%]">
                            <img src="/images/onboarding/onboarding-linkedin-download-archive.jpg" alt="LinkedIn archive options" />
                        </div>
                        <p>
                            Select &quot;Download larger data archive&quot; to retrieve your full
                            LinkedIn data report, or choose &quot;Want something in
                            particular?&quot; if you prefer a smaller data set, then
                            click &quot;Request.&quot;
                        </p>
                        <p className="font-bold pt-4 md:pt-8">You privacy matters to us</p>
                        <p className="mb-4">
                            Your privacy matters to us
                            We only ask for this data to help draft your bio.
                            We won&apos;t store it or use it for any other purpose,
                            and you can delete it at any time.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={1} totalSteps={5} isModal={isModal}>
            <div className="w-full py-2 px-2 sm:px-5">
                <BioPageHeader
                    header="Import your career history."
                    subHeader="Help us draft your bio by providing one or more of the files listed below."
                    subHeaderWidth="max-w-[710px]"
                />
                {
                    formError !== '' && (
                        <div className="w-full my-[20px]">
                            <WarningCard message={formError} />
                        </div>
                    )
                }
                <div className="flex flex-col gap-5 pb-10">
                    <FilePanel
                        onClickAdd={() => handleAddFileClick('linkedIn')}
                        onClickRemove={() => handleFileRemoveClick('linkedIn')}
                        onClickEdit={() => handleFileEditClick('linkedIn')}
                        fileName={importData.linkedIn?.name}
                        labelText="LinkedIn Data"
                        subHeader="Export your data from your LinkedIn account, then upload the data file provided by LinkedIn."
                        openModal={setLinkedInHelpModalVisible}
                    />
                    <FilePanel
                        onClickAdd={() => handleAddFileClick('PDF')}
                        onClickRemove={() => handleFileRemoveClick('PDF')}
                        onClickEdit={() => handleFileEditClick('PDF')}
                        fileName={importData.PDF?.name}
                        labelText="PDF"
                        subHeader="Upload your resume or another PDF."
                    />
                    {/* Start copy and paste panel */}
                    {
                        isCopyAndPasteActive
                            ? (
                                <div className="flex flex-col gap-4 bg-gray-100 border border-gray-200 rounded-md drop-shadow-sm min-h-[198px] w-full p-4">
                                    <BioLabel
                                        header="Copy and paste"
                                        subHeader="Copy and paste biographical text from another source."
                                    />
                                    <Textarea
                                        id="bio-plain-text"
                                        className="h-[109px] resize-none"
                                        onChange={(e) => handleCopyAndPasteChange(e.target.value)}
                                        onBlur={() => setIsCopyAndPasteActive(false)}
                                        value={importData.copyAndPaste}
                                        autoFocus={focusCopyAndPaste}
                                        theme={customTextAreaTheme}
                                    />
                                </div>
                            )
                            : (
                                <div className="flex flex-col gap-4 bg-gray-100 border border-gray-200 rounded-md drop-shadow-sm min-h-[198px] w-full p-4">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <img src="/images/bio/chart-bars-3-from-left.png" alt="pdf" className="h-3 w-3" />
                                            <p>Pasted Text</p>
                                        </div>
                                        <ButtonSansStyle
                                            onClick={() => handleCopyAndPasteEditClick()}
                                        >
                                            <img src="/images/bio/edit-outline.png" alt="edit" className="h-3 w-3 hover:cursor-pointer" />
                                        </ButtonSansStyle>
                                    </div>
                                    <div className="ml-0 sm:ml-[25px]">
                                        <p className="w-full md:w-full break-normal overflow-hidden text-ellipsis">{importData.copyAndPaste || 'Click edit to paste info.'}</p>
                                    </div>
                                </div>
                            )
                    }
                </div>
                <div className="relative -mr-4 sm:mt-16 -mb-0 sm:-mb-10">
                    <NavButtons
                        containerStyles={personalizeStyles.buttonGroupWithout}
                        handleBackNavigation={handleBackNavigation}
                        handleContinue={handleContinue}
                        isLoading={isLoading}
                    />
                </div>

                {renderLinkedInModal()}
                <Modal className="absolute z-[1000]" dismissible theme={customModalTheme} show={Boolean(modalType)} onClose={() => setModalType(null)} position="center" size="sm">
                    <p className="text-[20px] font-bold">File Upload</p>
                    <div className="flex items-center justify-center w-full">
                        <label htmlFor="dropzone-file" className="relative flex flex-col items-center justify-center w-full h-auto border-2 border-gray-200 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <img src="/images/bio/upload.png" alt="upload" className="h-5 w-5 mb-2" />
                                <p className="text-center text-base font-normal leading-7">Drag files here to upload</p>
                                <p className="text-noonBlue text-center text-xs font-normal underline leading-6">or browse for files</p>
                            </div>
                            <input id="dropzone-file" type="file" accept={modalType === 'PDF' ? '.pdf' : '.zip'} className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer" onChange={handleFileChange} />
                        </label>
                    </div>
                </Modal>
                {progress !== null && !checkBios && (
                    <Modal className="absolute z-[1000]" theme={customModalTheme} show={progress !== null} position="center" size="sm">
                        <p className="text-[20px] font-bold">Uploading Files...</p>
                        <div className="flex items-center gap-3">
                            <File className="w-4 h-4 float-left mr-0 text-cleanSlate" />
                            <div className="flex items-center justify-between w-full">
                                <p className="p-0 text-cleanSlate text-xs font-normal">
                                    {`${progress.completeInMb} MB`}
                                </p>
                                <span className="text-cleanSlate text-xs">
                                    {progress.complete}
                                    %
                                </span>
                            </div>
                        </div>
                        <ProgressBar progress={progress.complete} />
                    </Modal>
                )}
                {checkBios && (
                    <Modal className="absolute z-[1000]" theme={customModalTheme} show={checkBios} position="center" size="sm">
                        <p className="text-[20px] font-bold">Analyzing your upload...</p>
                        <p>We&apos;re processing your upload. Just a moment...</p>
                        <div className="flex items-center justify-center">
                            <Spinner size="xl" />
                        </div>
                        {generateTimeout
                            && (
                                <>
                                    <p>Taking too long?</p>
                                    <Button
                                        color="dark"
                                        className="bg-cleanSlate"
                                        onClick={skipGenerate}
                                    >
                                        Skip
                                    </Button>
                                </>
                            )}
                    </Modal>
                )}
                {noDataModal && (
                    <Modal className="absolute z-[1000]" theme={customModalTheme} show={noDataModal} position="center" size="sm">
                        <p className="text-[20px] font-bold">No data found</p>
                        <p>
                            We couldn&apos;t extract any useful
                            information from the documents you provided.
                        </p>

                        <p>Skip to manual entry</p>
                        <Button
                            color="dark"
                            className="bg-cleanSlate"
                            onClick={skipGenerate}
                        >
                            Manual Entry
                        </Button>
                    </Modal>
                )}
            </div>
        </PanelContainer>
    );
}

export default SelectImportMethodPanel;
